/**
 * All user-visible chrome outside the video.js control bar: title bar, live
 * badge, viewer count, offline overlay.
 *
 * Every user-supplied string (title, offline text) is rendered with
 * textContent — never innerHTML — so a hostile stream title can't become XSS.
 */

import type { PlayerConfig } from './config'

/** Viewer-count eye icon (icons8), inlined so no runtime asset fetch. Static markup only. */
const VIEWERS_ICON_SVG =
  '<svg viewBox="0 0 30 30" fill="currentColor" aria-hidden="true">' +
  '<path d="M15,5c-8.9183,0 -14.67901,9.21118 -14.7832,9.37891c-0.14024,0.17666 -0.21665,0.39554 ' +
  '-0.2168,0.62109c0.00018,0.1935 0.05649,0.38279 0.16211,0.54492c0.0013,0.00196 0.0026,0.00391 ' +
  '0.00391,0.00586c0.01719,0.03548 4.85282,9.44922 14.83398,9.44922c9.93882,0 14.76733,-9.32126 ' +
  '14.82617,-9.43555c0.00398,-0.00647 0.00789,-0.01298 0.01172,-0.01953c0.10562,-0.16213 ' +
  '0.16193,-0.35142 0.16211,-0.54492c-0.00004,-0.22465 -0.07572,-0.44275 -0.21484,-0.61914c' +
  '-0.00065,-0.00065 -0.0013,-0.0013 -0.00195,-0.00195c-0.10419,-0.16773 -5.86491,-9.37891 ' +
  '-14.7832,-9.37891zM15,8c3.866,0 7,3.134 7,7c0,3.866 -3.134,7 -7,7c-3.866,0 -7,-3.134 -7,-7c0,' +
  '-3.866 3.134,-7 7,-7zM15,12c-1.65685,0 -3,1.34315 -3,3c0,1.65685 1.34315,3 3,3c1.65685,0 ' +
  '3,-1.34315 3,-3c0,-1.65685 -1.34315,-3 -3,-3z"/></svg>'

/** Muted-speaker icon (lucide volume-x), static markup. */
const MUTED_ICON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
  '<line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>'

export interface OfflineOverride {
  title: string
  subtitle: string
  label?: string
  /**
   * What kind of dead end this is. `offline` is the broadcast having ended;
   * `retrying` is us still working on it; `error` is us having given up. They
   * read differently because they mean different things to the viewer.
   */
  tone?: 'offline' | 'retrying' | 'error'
  action?: { label: string; onClick: () => void }
}

/** What the tab says before an embed has a name, and after it loses one. */
const DEFAULT_DOCUMENT_TITLE = 'Livepush Player'

/**
 * Name the browser tab after the stream.
 *
 * Only a non-empty title is ever written. A denial carries branding-only config
 * with the title blanked on purpose (an unauthorised site must not learn what
 * the stream is), so falling back to the default here is what keeps the tab
 * from leaking what the page itself is withholding.
 */
function setDocumentTitle(title: string | null | undefined): void {
  const next = title?.trim() || DEFAULT_DOCUMENT_TITLE
  if (document.title !== next) document.title = next
}

export class OverlayManager {
  private readonly app: HTMLElement
  private config: PlayerConfig
  /** Viewer-count pills can exist in several places (title bar, pause overlay) */
  private viewerWraps: HTMLElement[] = []
  private viewerCounts: HTMLElement[] = []
  private offlineEl: HTMLElement | null = null
  private unmutePillEl: HTMLButtonElement | null = null
  private tapUnmuteEl: HTMLElement | null = null

  constructor(app: HTMLElement, config: PlayerConfig) {
    this.app = app
    this.config = config

    // Theme selection: scopes all [data-lp-theme='...'] rules in themes.css
    app.dataset.lpTheme = config.theme

    setDocumentTitle(config.title)

    if (config.color) {
      // Value is pre-validated by sanitizeColor(); set via the CSSOM, not a <style> tag.
      document.documentElement.style.setProperty('--theme-color', config.color)
      // Mirrored under a second name so themes with their own default accent
      // (noir) can tell "the embed chose this colour" from "nobody chose one".
      document.documentElement.style.setProperty('--theme-color-set', config.color)
    }
    if (config.posterUrl?.startsWith('http')) {
      app.classList.add('with-poster')
      const cover = app.querySelector<HTMLElement>('.video-cover')
      if (cover) cover.style.backgroundImage = `url(${CSS.escape(config.posterUrl)})`
    }
    if (config.audioOnly) app.classList.add('audio-only')
    if (config.uninterrupted) document.body.classList.add('uninterrupted')
    if (!config.autoplay) app.classList.add('no-autoplay')
  }

  /**
   * Adopt a changed config without rebuilding anything.
   *
   * Everything here is a text node, a CSS custom property or a class: the video
   * element is never touched, so a broadcaster renaming a stream or changing a
   * theme costs the viewer nothing. Structural changes — the live badge or the
   * viewer pill appearing or disappearing — need the title bar remounted, so we
   * report whether that is required rather than doing it here, since only the
   * controller knows whether a player currently exists.
   */
  updateConfig(next: PlayerConfig): { chromeNeedsRemount: boolean } {
    const prev = this.config
    this.config = next

    if (next.theme !== prev.theme) this.app.dataset.lpTheme = next.theme

    if (next.color !== prev.color) {
      const root = document.documentElement.style
      if (next.color) {
        root.setProperty('--theme-color', next.color)
        root.setProperty('--theme-color-set', next.color)
      } else {
        root.removeProperty('--theme-color')
        root.removeProperty('--theme-color-set')
      }
    }

    if (next.posterUrl !== prev.posterUrl) {
      const cover = this.app.querySelector<HTMLElement>('.video-cover')
      if (next.posterUrl?.startsWith('http')) {
        this.app.classList.add('with-poster')
        if (cover) cover.style.backgroundImage = `url(${CSS.escape(next.posterUrl)})`
      } else {
        this.app.classList.remove('with-poster')
        if (cover) cover.style.backgroundImage = ''
      }
    }

    if (next.uninterrupted !== prev.uninterrupted) {
      document.body.classList.toggle('uninterrupted', next.uninterrupted)
    }

    if (next.title !== prev.title) {
      for (const el of this.app.querySelectorAll<HTMLElement>('.lp-title, .lp-paused-title')) {
        el.textContent = next.title
      }
      setDocumentTitle(next.title)
    }
    if (next.description !== prev.description) {
      const desc = this.app.querySelector<HTMLElement>('.lp-paused-desc')
      if (desc) desc.textContent = next.description
    }

    // A visible offline card is showing copy that may have just changed.
    if (this.offlineEl && (next.offlineTitle !== prev.offlineTitle || next.offlineSubtitle !== prev.offlineSubtitle)) {
      const h1 = this.offlineEl.querySelector<HTMLElement>('h1')
      const p = this.offlineEl.querySelector<HTMLElement>('p')
      if (h1 && h1.textContent === prev.offlineTitle) h1.textContent = next.offlineTitle
      if (p && p.textContent === prev.offlineSubtitle) p.textContent = next.offlineSubtitle
    }

    // The badges live in two places (title bar and pause overlay) and the
    // pause overlay is skipped entirely when there is no title and no
    // description, so a config that adds either one has to rebuild it.
    const badgesChanged =
      next.showLiveBadge !== prev.showLiveBadge || next.showViewersCount !== prev.showViewersCount
    const pausedOverlayAppeared =
      !!(next.title || next.description) !== !!(prev.title || prev.description)

    return { chromeNeedsRemount: badgesChanged || pausedOverlayAppeared }
  }

  /** Title bar lives inside the player element so fullscreen keeps it visible. */
  mountTitleBar(playerEl: Element): void {
    playerEl.querySelector('.lp-title-bar')?.remove()
    // Fresh mount cycle: drop references to elements from a disposed player
    this.viewerWraps = this.viewerWraps.filter((el) => el.isConnected)
    this.viewerCounts = this.viewerCounts.filter((el) => el.isConnected)

    const bar = document.createElement('div')
    bar.className = 'lp-title-bar'

    // Line 1: title. Line 2: LIVE badge + viewer count.
    const title = document.createElement('h2')
    title.className = 'lp-title'
    title.textContent = this.config.title
    bar.appendChild(title)

    const meta = document.createElement('div')
    meta.className = 'lp-title-meta'
    bar.appendChild(meta)

    if (this.config.showLiveBadge) {
      const badge = document.createElement('span')
      badge.className = 'lp-live-badge'
      badge.textContent = 'LIVE'
      meta.appendChild(badge)
    }

    if (this.config.showViewersCount) {
      meta.appendChild(this.buildViewersPill())
    }

    playerEl.appendChild(bar)
  }

  /** Frosted viewers pill; hidden until the first count arrives. */
  private buildViewersPill(): HTMLElement {
    const wrap = document.createElement('span')
    wrap.className = 'lp-viewers'
    wrap.style.visibility = 'hidden'

    const icon = document.createElement('span')
    icon.className = 'lp-viewers-icon'
    icon.setAttribute('aria-hidden', 'true')
    // Static SVG markup only — no user-supplied strings go through innerHTML.
    icon.innerHTML = VIEWERS_ICON_SVG

    const count = document.createElement('span')
    count.className = 'lp-viewers-count'
    count.textContent = '0'

    wrap.append(icon, count)
    this.viewerWraps.push(wrap)
    this.viewerCounts.push(count)
    return wrap
  }

  /**
   * Netflix-style pause overlay: title + description revealed lower-left while
   * the player is paused. Visibility is pure CSS, driven by video.js's
   * vjs-paused/vjs-has-started state classes — no JS listeners to leak.
   */
  mountPausedOverlay(playerEl: Element): void {
    playerEl.querySelector('.lp-paused-overlay')?.remove()
    if (!this.config.title && !this.config.description) return

    const overlay = document.createElement('div')
    overlay.className = 'lp-paused-overlay'

    const box = document.createElement('div')
    box.className = 'lp-paused-info'

    if (this.config.title) {
      const h1 = document.createElement('h1')
      h1.className = 'lp-paused-title'
      h1.textContent = this.config.title
      box.appendChild(h1)
    }

    if (this.config.description) {
      const p = document.createElement('p')
      p.className = 'lp-paused-desc'
      p.textContent = this.config.description
      box.appendChild(p)
    }

    // LIVE badge + viewers below the description — each only if not disabled
    if (this.config.showLiveBadge || this.config.showViewersCount) {
      const meta = document.createElement('div')
      meta.className = 'lp-paused-meta'

      if (this.config.showLiveBadge) {
        const badge = document.createElement('span')
        badge.className = 'lp-live-badge'
        badge.textContent = 'LIVE'
        meta.appendChild(badge)
      }

      if (this.config.showViewersCount) {
        meta.appendChild(this.buildViewersPill())
      }

      box.appendChild(meta)
    }

    overlay.appendChild(box)
    playerEl.appendChild(overlay)
  }

  /**
   * "Tap to unmute" pill, top-right. Shown while playback is running muted;
   * one click restores sound. Visibility is driven by the controller.
   */
  mountUnmutePill(playerEl: Element, onUnmute: () => void): void {
    playerEl.querySelector('.lp-unmute-pill')?.remove()

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'lp-unmute-pill lp-hidden'
    btn.setAttribute('aria-label', 'Unmute')

    const icon = document.createElement('span')
    icon.className = 'lp-unmute-icon'
    // Static SVG markup only — no user-supplied strings go through innerHTML.
    icon.innerHTML = MUTED_ICON_SVG

    const label = document.createElement('span')
    label.textContent = 'Tap to unmute'

    btn.append(icon, label)
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation()
      onUnmute()
    })

    playerEl.appendChild(btn)
    this.unmutePillEl = btn

    // Full-surface tap target: while the player is in its first muted
    // autoplay, a click ANYWHERE unmutes instead of pausing (the pattern
    // used by X/Instagram). It sits under the control bar, so the transport
    // controls keep working normally.
    playerEl.querySelector('.lp-tap-unmute')?.remove()
    const layer = document.createElement('div')
    layer.className = 'lp-tap-unmute lp-hidden'
    layer.setAttribute('aria-hidden', 'true')
    layer.addEventListener('click', (ev) => {
      ev.preventDefault()
      ev.stopPropagation()
      onUnmute()
    })
    playerEl.appendChild(layer)
    this.tapUnmuteEl = layer
  }

  /** The pill shows whenever the player is silent — however it got there. */
  setUnmuteVisible(visible: boolean): void {
    this.unmutePillEl?.classList.toggle('lp-hidden', !visible)
  }

  /**
   * Full-surface tap target + the looping attention nudge. Only for the FIRST
   * muted playback: a viewer who muted deliberately still gets the pill, but
   * clicking the picture pauses as usual and the pill sits quietly.
   */
  setTapToUnmute(enabled: boolean): void {
    this.tapUnmuteEl?.classList.toggle('lp-hidden', !enabled)
    this.unmutePillEl?.classList.toggle('lp-nudge', enabled)
  }

  setViewerCount(count: number): void {
    const text = String(Math.max(count, 1))
    for (const el of this.viewerCounts) el.textContent = text
    for (const el of this.viewerWraps) el.style.visibility = 'visible'
  }

  /**
   * @param override Copy for causes that are not "the broadcast ended" — a
   * viewer whose own connection dropped is told that, not that the stream is
   * over, because those need different actions from them.
   */
  showOffline(override?: OfflineOverride): void {
    this.app.classList.add('offline')
    this.app.classList.remove('online')

    const tone = override?.tone ?? 'offline'

    // `showOfflineText: false` is the broadcaster saying "don't announce that
    // I'm not live" — it is not permission to leave a viewer staring at a black
    // box after a failure they could act on. Failures always speak.
    if (!this.config.showOfflineText && tone === 'offline') return

    // A different cause needs different copy — replace what is already there.
    if (this.offlineEl) {
      if (!override) return
      this.offlineEl.remove()
      this.offlineEl = null
    }

    const overlay = document.createElement('div')
    overlay.className = 'lp-offline-overlay'

    const card = document.createElement('div')
    card.className = 'lp-offline-card'
    if (tone !== 'offline') card.classList.add(`lp-card-${tone}`)

    const chip = document.createElement('span')
    chip.className = 'lp-offline-chip'
    const dot = document.createElement('span')
    dot.className = 'lp-offline-dot'
    chip.append(dot, document.createTextNode(override?.label ?? 'Offline'))

    const h1 = document.createElement('h1')
    h1.textContent = override?.title ?? this.config.offlineTitle
    const p = document.createElement('p')
    p.textContent = override?.subtitle ?? this.config.offlineSubtitle

    card.append(chip, h1, p)

    // A dead end the viewer can act on beats a dead end they can only stare at.
    if (override?.action) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'lp-card-action'
      button.textContent = override.action.label
      button.addEventListener('click', override.action.onClick)
      card.appendChild(button)
    }

    overlay.appendChild(card)

    const host = this.app.querySelector('.video-cover-wrapper') ?? this.app
    host.appendChild(overlay)
    this.offlineEl = overlay
  }

  hideOffline(): void {
    this.app.classList.add('online')
    this.app.classList.remove('offline')
    this.offlineEl?.remove()
    this.offlineEl = null
  }

  /**
   * The LIVE badge is a claim about the BROADCAST, not about this player.
   *
   * It used to render purely from `showLiveBadge`, which meant it appeared the
   * moment a player was mounted. That is not the same thing as a stream being
   * live: `hideOffline` deliberately runs on attach rather than on the first
   * frame (see the comment at its call site), so there is a real window where
   * the cover image is gone, the chrome is up and nothing is being broadcast.
   * A customer put that player on a holding page a week before a funeral
   * service and his viewers saw a red LIVE badge over the cover image.
   *
   * The honest source of truth is the status poller, which is the only thing
   * that actually knows. Driving the badge from it also keeps the badge on the
   * pre-start and paused presentations of a stream that IS live, which is
   * correct and which gating on "frames are moving" would have broken.
   */
  setStreamLive(live: boolean): void {
    this.app.classList.toggle('lp-stream-live', live)
  }
}
